<template>
  <li
    class="group/lang mb-2 flex cursor-pointer items-center justify-between gap-6 rounded-md border-2 border-solid border-transparent bg-gray-500 p-2 font-inter text-gray-100 transition duration-300 last:!mb-0 hover:bg-gray-400 hover:text-gray-50 focus:border-accent-1"
    :class="{
      '!border-confirm-1 !bg-gray-700 !text-gray-10': isCurrentLanguage,
    }"
    :data-testid="`language-item-${lang.key}`"
  >
    <div class="flex items-center gap-1">
      <div
        class="grid h-5 w-5 items-center justify-center"
        :class="{
          'text-confirm-1': isCurrentLanguage,
          'text-gray-100': !isCurrentLanguage,
        }"
      >
        <Pipe v-if="isCurrentLanguage" data-testid="selected" />
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="14"
          height="14"
        >
          <path
            d="M352 256c0 22.2-1.2 43.6-3.3 64l-185.3 0c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64l185.3 0c2.2 20.4 3.3 41.8 3.3 64zm28.8-64l123.1 0c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64l-123.1 0c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32l-116.7 0c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0l-176.6 0c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0L18.6 160C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192l123.1 0c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64L8.1 320C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6l176.6 0c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352l116.7 0zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6l116.7 0z"
            fill="currentColor"
          />
        </svg>
      </div>
      <div class="leading-4">
        <span>{{ languageName }}</span>
        <br />
        <span
          class="leading-2 text-[10px] text-gray-200 transition duration-300 group-hover/lang:!text-gray-100"
          :class="{
            '!text-gray-100': isCurrentLanguage,
          }"
          >{{ lang.key }}</span
        >
      </div>
    </div>

    <div v-if="!isPreview">
      <DefineTemplate>
        <div
          v-if="openAiEnabled"
          :class="{
            'flex items-center': showSourcePopover,
          }"
        >
          <TButton
            v-tooltip="{
              content: showSourcePopover
                ? $t('layout.translate_from_primary')
                : $t('layout.translate_to_this_language'),
            }"
            data-testid="translate"
            icon-only
            :class="{
              '!bg-gray-400 hover:!bg-gray-600 hover:!text-gray-50 group-hover/lang:!bg-gray-500':
                !isCurrentLanguage,
              '!rounded-r-none': showSourcePopover,
            }"
            @click.stop="
              $emit('translateLanguage', showSourcePopover ? primaryKey : null)
            "
          >
            <template #icon>
              <Translate />
            </template>
          </TButton>
          <VDropdown
            v-if="showSourcePopover"
            placement="bottom-end"
            :distance="6"
            popper-class="translate-from-popper"
          >
            <TButton
              v-tooltip="{ content: $t('layout.translate_from') }"
              data-testid="translate-source-more"
              icon-only
              class="!rounded-l-none !border-l !border-solid !border-gray-600 !px-0.5"
              :class="{
                '!bg-gray-400 hover:!bg-gray-600 hover:!text-gray-50 group-hover/lang:!bg-gray-500':
                  !isCurrentLanguage,
              }"
            >
              <template #icon>
                <font-awesome-icon :icon="['fas', 'caret-down']" />
              </template>
            </TButton>
            <template #popper="{ hide }">
              <div
                data-testid="translate-popover"
                class="w-[200px] overflow-hidden rounded-md border border-solid border-gray-500 bg-gray-700 py-1 font-inter shadow-lg"
                @click.stop
              >
                <div
                  class="px-3 py-1 text-[11px] font-medium leading-4 text-gray-200"
                >
                  {{ $t("layout.translate_from") }}
                </div>
                <PerfectScrollbar
                  style="position: relative"
                  :style="{ height: sourceListHeight + 'px' }"
                  :settings="{ wheelPropagation: false }"
                >
                  <button
                    v-for="src in sourceOptions"
                    :key="src.value"
                    type="button"
                    :data-testid="`translate-source-${src.value}`"
                    class="flex w-full cursor-pointer items-center border-0 bg-transparent px-3 py-1.5 text-left text-xs font-medium leading-4 text-gray-100 transition duration-200 hover:bg-gray-500 hover:text-gray-50"
                    @click="selectSource(src.value, hide)"
                  >
                    {{ src.label }}
                  </button>
                </PerfectScrollbar>
              </div>
            </template>
          </VDropdown>
        </div>
      </DefineTemplate>
      <div v-if="isPrimaryLanguage" class="flex items-center gap-2" @click.stop>
        <div
          class="rounded-md bg-accent-1 px-1.5 py-0.5 text-[11px] font-medium text-gray-50"
          data-testid="primary-language-badge"
        >
          {{ $t("layout.primary_language") }}
        </div>
        <ReuseTemplate />
      </div>
      <div v-else class="flex items-center gap-2" @click.stop>
        <div
          class="rounded-md px-2 leading-6 text-gray-100 transition duration-300"
          :class="{
            'bg-gray-500 hover:bg-gray-400 hover:!text-gray-10':
              isCurrentLanguage,
            'bg-gray-400 hover:!bg-gray-600 hover:!text-gray-50 group-hover/lang:bg-gray-500':
              !isCurrentLanguage,
          }"
          data-testid="set-primary-language-button"
          @click.stop="$emit('setPrimaryLanguage')"
        >
          {{ $t("layout.set_primary_language") }}
        </div>
        <ReuseTemplate />
        <TButton
          data-testid="delete"
          icon-only
          danger
          @click.stop="$emit('deleteLanguage')"
        >
          <template #icon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.4688 4.9375C15.8242 4.9375 16.125 5.23828 16.125 5.59375C16.125 5.97656 15.8242 6.25 15.4688 6.25H15.1406L14.4844 15.1367C14.4023 16.0664 13.6641 16.75 12.7344 16.75H7.23828C6.30859 16.75 5.57031 16.0664 5.48828 15.1367L4.83203 6.25H4.53125C4.14844 6.25 3.875 5.97656 3.875 5.59375C3.875 5.23828 4.14844 4.9375 4.53125 4.9375H6.41797L7.42969 3.43359C7.70312 3.02344 8.19531 2.75 8.71484 2.75H11.2578C11.7773 2.75 12.2695 3.02344 12.543 3.43359L13.5547 4.9375H15.4688ZM8.71484 4.0625C8.63281 4.0625 8.55078 4.11719 8.52344 4.17188L8.00391 4.9375H11.9688L11.4492 4.17188C11.4219 4.11719 11.3398 4.0625 11.2578 4.0625H8.71484ZM13.8281 6.25H6.14453L6.80078 15.0547C6.82812 15.2734 7.01953 15.4375 7.23828 15.4375H12.7344C12.9531 15.4375 13.1445 15.2734 13.1719 15.0547L13.8281 6.25Z"
                fill="currentColor"
              />
            </svg>
          </template>
        </TButton>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
  import { TButton } from "@topol/twig-ui";
  import { Pipe, Translate } from "@topol/icons";
  import { IMultilingualLanguage } from "@/schemas/language/languageSchema";
  import { createReusableTemplate } from "@vueuse/core";
  import { PerfectScrollbar } from "vue3-perfect-scrollbar";
  import { computed } from "vue";
  import { useI18n } from "vue-i18n";

  const props = defineProps<{
    langsToUse: IMultilingualLanguage;
    lang: {
      key: string;
      primary: boolean;
    };
    languages: { key: string; primary: boolean }[];
    isCurrentLanguage: boolean;
    isPreview: boolean;
    isPrimaryLanguage: boolean;
    openAiEnabled?: boolean;
  }>();

  const emit = defineEmits([
    "setPrimaryLanguage",
    "deleteLanguage",
    "translateLanguage",
  ]);
  const [DefineTemplate, ReuseTemplate] = createReusableTemplate();

  const { t } = useI18n();

  function languageNameByKey(key: string) {
    const found = props.langsToUse.find((l) => l.code === key);
    return found ? found.name : key;
  }

  const languageName = computed(() => languageNameByKey(props.lang.key));

  const showSourcePopover = computed(() => (props.languages?.length ?? 0) > 1);

  const primaryKey = computed(
    () =>
      props.languages?.find((l) => l.primary)?.key ??
      props.languages?.[0]?.key ??
      ""
  );

  const sourceOptions = computed(() =>
    (props.languages ?? []).map((l) => ({
      label: l.primary
        ? `${languageNameByKey(l.key)} (${t("layout.primary_language")})`
        : languageNameByKey(l.key),
      value: l.key,
    }))
  );

  const SOURCE_ROW_HEIGHT = 28;
  const sourceListHeight = computed(() =>
    Math.min(sourceOptions.value.length * SOURCE_ROW_HEIGHT, 240)
  );

  function selectSource(key: string, hide: () => void) {
    emit("translateLanguage", key);
    hide();
  }
</script>

<style>
  .translate-from-popper .v-popper__inner {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    overflow: visible;
  }

  .translate-from-popper .v-popper__arrow-container {
    display: none;
  }
</style>
